import { BLEND_MODE_IDS } from '../core/blendModes';
import { mat3Identity, type Mat3 } from '../core/math';
import {
  blendIfChannelIndex,
  blendIfRangeToUniform,
  cloneBlendIf,
} from '../core/blendIf';
import type { BlendChannels, BlendIf, BlendMode, Point, RGBA } from '../core/types';
import { DEFAULT_BLEND_CHANNELS } from '../core/types';
import { Framebuffer, PingPong } from './framebuffer';
import { MeshGeometry } from './mesh';
import { ProgramCache } from './program';
import { QuadGeometry, QUAD_TO_TARGET } from './quad';
import { shaderSources } from './shaders';
import { Texture } from './texture';

export interface LayerDraw {
  texture: Texture;
  mask: Texture | null;
  /** False for an unlinked mask: it keeps its place while the layer moves. */
  maskFollows?: boolean;
  opacity: number;
  blendMode: BlendMode;
  /** Which result channels this layer may change; default all. */
  blendChannels?: BlendChannels;
  /** Advanced Blending → Blend If; default is fully open. */
  blendIf?: BlendIf;
  /** Destination-UV to source-UV matrix, for live move/transform previews. */
  srcMatrix?: Mat3 | null;
}

export interface ChannelViewDraw {
  /** 0..2 for a colour channel, 3 for `overlay`, -1 to keep the colours. */
  grey: number;
  colorMask: readonly [number, number, number];
  /** Single-channel texture: the grey subject, or the mask over the colours. */
  overlay: Texture | null;
  overlayColor: RGBA;
}

export interface AdjustmentPass {
  /** See `ADJUSTMENT_TYPES`. */
  type: number;
  /** `PARAM_SLOTS` vec4s, flattened. */
  params: Float32Array;
  /** 256x1 lookup row for the adjustments that are a table. */
  lut?: Texture | null;
  /** Blurred copy of the source, for the ones that need a local average. */
  aux?: Texture | null;
}

export interface StampParams {
  /** UV distance where a generated circular tip starts to fall off. */
  inner: number;
  /** 0..1 coverage of a single dab. */
  flow: number;
  /** Sampled tip, or null for a generated circle. */
  tip?: Texture | null;
  /** Pencil mode: a binary edge instead of an antialiased one. */
  sharp?: boolean;
}

export interface StrokeFilterParams {
  type: number;
  /** 0..1 strength; Sponge takes a negative value to desaturate. */
  amount: number;
  /** Precomputed second input: a blurred copy, or a frozen clone source. */
  source?: Texture | null;
  /** Clone Stamp source offset in document pixels. */
  offsetX?: number;
  offsetY?: number;
  /** Tonal band for Dodge and Burn; see `TONE_RANGE_IDS`. */
  range?: number;
  /**
   * Doc-space sampling shift for coverage/selection relative to layer-local UV
   * (layer contentOffset in pixels).
   */
  contentOffset?: Point;
  /** When false, coverage is already layer-local; selection still uses contentOffset. */
  coverageDocSpace?: boolean;
}

/** How doc-space masks map onto a layer-local destination. */
export interface StrokeSampleOffset {
  contentOffset?: Point;
  /** When false, coverage is layer-local; selection still uses contentOffset. */
  coverageDocSpace?: boolean;
}

export interface SmudgeParams {
  inner: number;
  strength: number;
  tip?: Texture | null;
  sharp?: boolean;
  /** Where the dab picks colour up from, in document pixels. */
  pickupX: number;
  pickupY: number;
  /** Doc-space shift for selection sampling (layer contentOffset). */
  contentOffset?: Point;
}

export interface GradientParams {
  /** See `GRADIENT_*` in `gradient.frag.glsl`. */
  type: number;
  start: { x: number; y: number };
  end: { x: number; y: number };
  /** Baked stops, `lutSize` texels wide and one tall. */
  lut: Texture;
  opacity: number;
  blendMode: BlendMode;
  reverse: boolean;
  dither: boolean;
  contentOffset?: Point;
}

export interface PatternFillParams {
  docWidth: number;
  docHeight: number;
  /** Document pixel the tile grid starts from. */
  originX: number;
  originY: number;
  opacity: number;
  blendMode: BlendMode;
  /**
   * Document-pixel size of one tile. Defaults to the pattern texture's size;
   * a fill layer passes a scaled size so the tile can be resized without a
   * second upload.
   */
  tileWidth?: number;
  tileHeight?: number;
  contentOffset?: Point;
}

function strokeSampleUv(
  sample: StrokeSampleOffset,
  width: number,
  height: number,
): { coverage: Float32Array; selection: Float32Array } {
  const ox = (sample.contentOffset?.x ?? 0) / width;
  const oy = (sample.contentOffset?.y ?? 0) / height;
  const selection = new Float32Array([ox, oy]);
  const coverageDoc = sample.coverageDocSpace !== false;
  return {
    coverage: coverageDoc ? selection : new Float32Array([0, 0]),
    selection,
  };
}

/**
 * Owns every GL draw call. Resource lifetime and undo bookkeeping live in the
 * engine; this class only knows how to run passes.
 */
export class Renderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly programs: ProgramCache;
  private readonly quad: QuadGeometry;
  private readonly mesh: MeshGeometry;
  private readonly identity = mat3Identity();
  /** Fully opaque 1x1 R8 texture, used where a shader wants an optional mask. */
  private readonly whiteMask: Texture;

  /** Exposed for passes that need an all-opaque coverage without allocating one. */
  get whiteMaskTexture(): Texture {
    return this.whiteMask;
  }

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.programs = new ProgramCache(gl);
    this.quad = new QuadGeometry(gl);
    this.mesh = new MeshGeometry(gl);
    this.whiteMask = Texture.create(gl, 1, 1, 'r8', 'nearest');
    this.whiteMask.uploadPixels({ x: 0, y: 0, width: 1, height: 1 }, new Uint8Array([255]));
  }

  /**
   * Composites layers bottom-to-top into `pingPong` and returns the texture that
   * holds the result. Each layer is a separate pass because blend modes need to
   * read the backdrop.
   */
  composite(layers: readonly LayerDraw[], pingPong: PingPong): Texture {
    const { gl } = this;
    gl.disable(gl.BLEND);
    pingPong.reset();
    pingPong.target.clear(0, 0, 0, 0);
    pingPong.swap();

    if (layers.length === 0) {
      // `source` is now the cleared texture from the pass above.
      return pingPong.source;
    }

    for (const layer of layers) {
      this.blendLayer(pingPong.target, pingPong.source, layer);
      pingPong.swap();
    }

    return pingPong.source;
  }

  /**
   * Blends one layer over a backdrop into a separate target. Callers that walk a
   * layer tree drive the buffers themselves, because an adjustment layer has to
   * read the backdrop accumulated so far.
   */
  blendLayer(target: Framebuffer, backdrop: Texture, layer: LayerDraw): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();

    const program = this.programs.get('composite', shaderSources.quadVert, shaderSources.composite);
    program.use();
    backdrop.bind(0);
    layer.texture.bind(1);
    (layer.mask ?? this.whiteMask).bind(2);

    const channels = layer.blendChannels ?? DEFAULT_BLEND_CHANNELS;
    const blendIf = cloneBlendIf(layer.blendIf);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uBackdrop: 0,
      uSource: 1,
      uMask: 2,
      uUseMask: layer.mask != null,
      uMaskFollows: layer.maskFollows !== false,
      uUseMatrix: Boolean(layer.srcMatrix),
      uSrcMatrix: layer.srcMatrix ?? this.identity,
      uOpacity: layer.opacity,
      uBlendMode: BLEND_MODE_IDS[layer.blendMode],
      uChannelMask: [
        channels.r ? 1 : 0,
        channels.g ? 1 : 0,
        channels.b ? 1 : 0,
        channels.a ? 1 : 0,
      ],
      uBlendIfChannel: blendIfChannelIndex(blendIf.channel),
      uBlendIfThis: blendIfRangeToUniform(blendIf.thisLayer),
      uBlendIfUnder: blendIfRangeToUniform(blendIf.underlying),
    });
    this.quad.draw();
  }

  /**
   * Rewrites a composited document into the view the Channels panel asks for:
   * one channel as greyscale, a subset of the colour channels, or a red mask laid
   * over them. See `channelView.frag.glsl`.
   */
  drawChannelView(target: Framebuffer, source: Texture, params: ChannelViewDraw): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'channelView',
      shaderSources.quadVert,
      shaderSources.channelView,
    );
    program.use();
    source.bind(0);
    (params.overlay ?? this.whiteMask).bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uOverlay: 1,
      uUseOverlay: params.overlay != null && params.grey < 0,
      uGrey: params.grey,
      uColorMask: new Float32Array(params.colorMask),
      uOverlayColor: new Float32Array([
        params.overlayColor.r,
        params.overlayColor.g,
        params.overlayColor.b,
        params.overlayColor.a,
      ]),
    });
    this.quad.draw();
  }

  /**
   * Confines the next draws to one colour channel, which is how a stroke paints
   * into Red, Green or Blue alone. `null` restores writing to all four.
   */
  setChannelWriteMask(channel: number | null): void {
    const { gl } = this;
    if (channel === null) {
      gl.colorMask(true, true, true, true);
      return;
    }
    gl.colorMask(channel === 0, channel === 1, channel === 2, false);
  }

  /** Writes the alpha channel of an RGBA texture into an R8 coverage target. */
  extractAlpha(target: Framebuffer, source: Texture): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'alphaToMask',
      shaderSources.quadVert,
      shaderSources.alphaToMask,
    );
    program.use();
    source.bind(0);
    program.setUniforms({ uTransform: QUAD_TO_TARGET, uSource: 0 });
    this.quad.draw();
  }

  /** Draws the composited document onto the canvas, over a checkerboard. */
  drawToScreen(
    image: Texture,
    viewMatrix: Mat3,
    zoom: number,
    canvasWidth: number,
    canvasHeight: number,
    checkerSize: number,
  ): void {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvasWidth, canvasHeight);
    gl.disable(gl.BLEND);
    gl.clearColor(0.13, 0.13, 0.14, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const program = this.programs.get('view', shaderSources.quadVert, shaderSources.view);
    program.use();
    image.bind(0);
    program.setUniforms({
      uTransform: viewMatrix,
      uImage: 0,
      uImageSize: new Float32Array([image.width, image.height]),
      uZoom: zoom,
      uCheckerSize: checkerSize,
    });
    this.quad.draw();
  }

  /** Dashed selection outline, drawn straight onto the canvas after the document. */
  drawSelectionOverlay(
    mask: Texture,
    viewMatrix: Mat3,
    texelX: number,
    texelY: number,
    phase: number,
    dash: number,
    srcMatrix: Mat3 | null = null,
  ): void {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.disable(gl.BLEND);
    // Channel-restricted strokes leave a narrow colorMask; restore full RGB.
    gl.colorMask(true, true, true, true);

    const program = this.programs.get(
      'marchingAnts',
      shaderSources.quadVert,
      shaderSources.marchingAnts,
    );
    program.use();
    mask.bind(0);
    program.setUniforms({
      uTransform: viewMatrix,
      uMask: 0,
      uTexel: new Float32Array([texelX, texelY]),
      uPhase: phase,
      uDash: dash,
      uUseMatrix: Boolean(srcMatrix),
      uSrcMatrix: srcMatrix ?? this.identity,
    });
    this.quad.draw();
  }

  /** Blit with an optional inverse transform and alpha scale. */
  copy(
    target: Framebuffer,
    source: Texture,
    options: { srcMatrix?: Mat3 | null; alpha?: number } = {},
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('copy', shaderSources.quadVert, shaderSources.copy);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uUseMatrix: Boolean(options.srcMatrix),
      uSrcMatrix: options.srcMatrix ?? this.identity,
      uAlpha: options.alpha ?? 1,
    });
    this.quad.draw();
  }

  /**
   * Draws a source region through a warp mesh into `target`, both of them
   * document-sized. `sample` gives, for grid parameters 0..1, the destination
   * point and the source point, both in document pixels.
   */
  warp(
    target: Framebuffer,
    source: Texture,
    width: number,
    height: number,
    cells: number,
    sample: (s: number, t: number) => { dest: readonly [number, number]; src: readonly [number, number] },
  ): void {
    const { gl } = this;
    this.mesh.upload(cells, (s, t) => {
      const { dest, src } = sample(s, t);
      // Destination goes straight to clip space; the source stays in UV.
      return {
        pos: [(dest[0] / width) * 2 - 1, (dest[1] / height) * 2 - 1],
        uv: [src[0] / width, src[1] / height],
      };
    });

    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('warp', shaderSources.warpVert, shaderSources.copy);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: mat3Identity(),
      uSource: 0,
      uUseMatrix: false,
      uSrcMatrix: this.identity,
      uAlpha: 1,
    });
    this.mesh.draw();
  }

  /**
   * Stamps one brush dab into an R8 coverage target. `matrix` places the unit
   * quad, so a stamp can be rotated and squeezed; see `stampMatrix`.
   *
   * At full flow the dabs are combined with MAX, which keeps a soft stroke free
   * of darker seams where consecutive dabs overlap. Below full flow they
   * accumulate like an airbrush, so passing over the same spot builds up.
   */
  drawStamp(target: Framebuffer, matrix: Mat3, stamp: StampParams): void {
    const { gl } = this;
    target.bind();
    gl.enable(gl.BLEND);
    const accumulate = stamp.flow < 1;
    if (accumulate) {
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR);
    } else {
      gl.blendEquation(gl.MAX);
    }

    const program = this.programs.get('brush', shaderSources.quadVert, shaderSources.brush);
    program.use();
    (stamp.tip ?? this.whiteMask).bind(0);
    program.setUniforms({
      uTransform: matrix,
      uInner: stamp.inner,
      uFlow: stamp.flow,
      uUseTip: stamp.tip != null,
      uSharp: stamp.sharp === true,
      uTip: 0,
    });
    this.quad.draw();

    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ZERO);
    gl.disable(gl.BLEND);
  }

  /** Composites a coverage mask onto a layer as paint (or as an erase). */
  applyStroke(
    target: Framebuffer,
    layer: Texture,
    coverage: Texture,
    selection: Texture | null,
    color: RGBA,
    opacity: number,
    erase: boolean,
    blendMode: BlendMode = 'normal',
    sample: StrokeSampleOffset = {},
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'strokeApply',
      shaderSources.quadVert,
      shaderSources.strokeApply,
    );
    program.use();
    layer.bind(0);
    coverage.bind(1);
    (selection ?? this.whiteMask).bind(2);
    const offsets = strokeSampleUv(sample, layer.width, layer.height);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uLayer: 0,
      uCoverage: 1,
      uSelection: 2,
      uUseSelection: selection !== null,
      uColor: new Float32Array([color.r, color.g, color.b, color.a]),
      uOpacity: opacity,
      uErase: erase,
      uBlendMode: BLEND_MODE_IDS[blendMode],
      uCoverageOffset: offsets.coverage,
      uSelectionOffset: offsets.selection,
    });
    this.quad.draw();
  }

  /**
   * Runs a per-pixel filter through the stroke's coverage: Blur, Sharpen, Dodge,
   * Burn, Sponge and Clone Stamp are all the same pass with a different `type`.
   */
  applyStrokeFilter(
    target: Framebuffer,
    base: Texture,
    coverage: Texture,
    selection: Texture | null,
    params: StrokeFilterParams,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'strokeFilter',
      shaderSources.quadVert,
      shaderSources.strokeFilter,
    );
    program.use();
    base.bind(0);
    coverage.bind(1);
    (selection ?? this.whiteMask).bind(2);
    (params.source ?? this.whiteMask).bind(3);

    const offsets = strokeSampleUv(params, base.width, base.height);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uBase: 0,
      uCoverage: 1,
      uSelection: 2,
      uSource: 3,
      uUseSelection: selection !== null,
      uUseSource: params.source != null,
      uType: params.type,
      uRange: params.range ?? 1,
      uAmount: params.amount,
      uOffset: new Float32Array([
        (params.offsetX ?? 0) / base.width,
        (params.offsetY ?? 0) / base.height,
      ]),
      uCoverageOffset: offsets.coverage,
      uSelectionOffset: offsets.selection,
    });
    this.quad.draw();
  }

  /**
   * Copies the part of `source` that `matrix` covers into `target` at the same
   * document position. Region passes exist so that per-dab work such as Smudge
   * touches only the pixels under the dab.
   */
  copyRegion(target: Framebuffer, source: Texture, matrix: Mat3): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('region', shaderSources.quadVert, shaderSources.region);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: matrix,
      uSource: 0,
      uResolution: new Float32Array([source.width, source.height]),
    });
    this.quad.draw();
  }

  /** One Smudge dab: drags the pixels behind the dab into it. */
  smudgeDab(
    target: Framebuffer,
    source: Texture,
    selection: Texture | null,
    matrix: Mat3,
    params: SmudgeParams,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('smudge', shaderSources.quadVert, shaderSources.smudge);
    program.use();
    source.bind(0);
    (selection ?? this.whiteMask).bind(1);
    (params.tip ?? this.whiteMask).bind(2);
    program.setUniforms({
      uTransform: matrix,
      uSource: 0,
      uSelection: 1,
      uTip: 2,
      uUseSelection: selection !== null,
      uUseTip: params.tip != null,
      uSharp: params.sharp === true,
      uInner: params.inner,
      uStrength: params.strength,
      uPickup: new Float32Array([params.pickupX / source.width, params.pickupY / source.height]),
      uResolution: new Float32Array([source.width, source.height]),
      uSelectionOffset: new Float32Array([
        (params.contentOffset?.x ?? 0) / source.width,
        (params.contentOffset?.y ?? 0) / source.height,
      ]),
    });
    this.quad.draw();
  }

  /** One Jacobi sweep of the healing solve; see `healJacobi.frag.glsl`. */
  healJacobi(
    target: Framebuffer,
    correction: Texture,
    base: Texture,
    source: Texture | null,
    mask: Texture,
    matrix: Mat3,
    step: number,
    offsetX: number,
    offsetY: number,
    init = false,
    contentOffset: Point = { x: 0, y: 0 },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'healJacobi',
      shaderSources.quadVert,
      shaderSources.healJacobi,
    );
    program.use();
    correction.bind(0);
    base.bind(1);
    (source ?? this.whiteMask).bind(2);
    mask.bind(3);
    program.setUniforms({
      uTransform: matrix,
      uCorrection: 0,
      uBase: 1,
      uSource: 2,
      uMask: 3,
      uUseSource: source != null,
      uInit: init,
      uOffset: new Float32Array([offsetX / base.width, offsetY / base.height]),
      uResolution: new Float32Array([base.width, base.height]),
      uStep: new Float32Array([step / base.width, step / base.height]),
      uMaskOffset: new Float32Array([contentOffset.x / base.width, contentOffset.y / base.height]),
    });
    this.quad.draw();
  }

  /** Writes `source + correction` into the healed region. */
  healApply(
    target: Framebuffer,
    base: Texture,
    source: Texture | null,
    correction: Texture,
    mask: Texture,
    selection: Texture | null,
    matrix: Mat3,
    offsetX: number,
    offsetY: number,
    contentOffset: Point = { x: 0, y: 0 },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('healApply', shaderSources.quadVert, shaderSources.healApply);
    program.use();
    base.bind(0);
    (source ?? this.whiteMask).bind(1);
    correction.bind(2);
    mask.bind(3);
    (selection ?? this.whiteMask).bind(4);
    program.setUniforms({
      uTransform: matrix,
      uBase: 0,
      uSource: 1,
      uCorrection: 2,
      uMask: 3,
      uSelection: 4,
      uUseSource: source != null,
      uUseSelection: selection !== null,
      uOffset: new Float32Array([offsetX / base.width, offsetY / base.height]),
      uResolution: new Float32Array([base.width, base.height]),
      uMaskOffset: new Float32Array([contentOffset.x / base.width, contentOffset.y / base.height]),
    });
    this.quad.draw();
  }

  /** Draws a gradient over `base`, honouring the selection and a blend mode. */
  drawGradient(
    target: Framebuffer,
    base: Texture,
    selection: Texture | null,
    params: GradientParams,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('gradient', shaderSources.quadVert, shaderSources.gradient);
    program.use();
    base.bind(0);
    params.lut.bind(1);
    (selection ?? this.whiteMask).bind(2);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uBase: 0,
      uLut: 1,
      uSelection: 2,
      uUseSelection: selection !== null,
      uReverse: params.reverse,
      uDither: params.dither,
      uType: params.type,
      uBlendMode: BLEND_MODE_IDS[params.blendMode],
      uOpacity: params.opacity,
      uStart: new Float32Array([params.start.x, params.start.y]),
      uEnd: new Float32Array([params.end.x, params.end.y]),
      uDocSize: new Float32Array([base.width, base.height]),
      uLutTexel: 1 / params.lut.width,
      uContentOffset: new Float32Array([
        (params.contentOffset?.x ?? 0) / base.width,
        (params.contentOffset?.y ?? 0) / base.height,
      ]),
    });
    this.quad.draw();
  }

  applyAdjustment(
    target: Framebuffer,
    source: Texture,
    selection: Texture | null,
    pass: AdjustmentPass,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('adjust', shaderSources.quadVert, shaderSources.adjust);
    program.use();
    source.bind(0);
    (selection ?? this.whiteMask).bind(1);
    (pass.lut ?? this.whiteMask).bind(2);
    (pass.aux ?? source).bind(3);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uSelection: 1,
      uUseSelection: selection !== null,
      uLut: 2,
      uUseLut: pass.lut != null,
      uAux: 3,
      uUseAux: pass.aux != null,
      uType: pass.type,
      uParams: pass.params,
    });
    this.quad.draw();
  }

  /** One axis of a separable Gaussian on an R8 target. */
  blurPass(
    target: Framebuffer,
    source: Texture,
    stepX: number,
    stepY: number,
    taps: number,
    sigma: number,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('blur', shaderSources.quadVert, shaderSources.blur);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uStep: new Float32Array([stepX, stepY]),
      uTaps: Math.max(0, Math.min(64, Math.round(taps))),
      uSigma: Math.max(0.1, sigma),
    });
    this.quad.draw();
  }

  /** One axis of a separable Gaussian on an RGBA target. */
  blurColorPass(
    target: Framebuffer,
    source: Texture,
    stepX: number,
    stepY: number,
    taps: number,
    sigma: number,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('blurColor', shaderSources.quadVert, shaderSources.blurColor);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uStep: new Float32Array([stepX, stepY]),
      uTaps: Math.max(0, Math.min(64, Math.round(taps))),
      uSigma: Math.max(0.1, sigma),
    });
    this.quad.draw();
  }

  combineMask(target: Framebuffer, current: Texture, shape: Texture, mode: number): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('combine', shaderSources.quadVert, shaderSources.combine);
    program.use();
    current.bind(0);
    shape.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uCurrent: 0,
      uShape: 1,
      uMode: mode,
    });
    this.quad.draw();
  }

  /** Multiplies a layer's alpha by a coverage texture, baking a mask in. */
  /** Tiles a pattern over a layer through a coverage mask. */
  applyPattern(
    target: Framebuffer,
    layer: Texture,
    coverage: Texture,
    pattern: Texture,
    params: PatternFillParams,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'patternApply',
      shaderSources.quadVert,
      shaderSources.patternApply,
    );
    program.use();
    layer.bind(0);
    coverage.bind(1);
    pattern.bind(2);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uLayer: 0,
      uCoverage: 1,
      uPattern: 2,
      uDocSize: new Float32Array([params.docWidth, params.docHeight]),
      uTileSize: new Float32Array([
        params.tileWidth ?? pattern.width,
        params.tileHeight ?? pattern.height,
      ]),
      uOrigin: new Float32Array([params.originX, params.originY]),
      uOpacity: params.opacity,
      uBlendMode: BLEND_MODE_IDS[params.blendMode],
      uCoverageOffset: new Float32Array([
        (params.contentOffset?.x ?? 0) / layer.width,
        (params.contentOffset?.y ?? 0) / layer.height,
      ]),
    });
    this.quad.draw();
  }

  /** Filter - Other - Offset: shifts the layer, wrapping or clearing the gap. */
  offset(
    target: Framebuffer,
    source: Texture,
    shiftX: number,
    shiftY: number,
    wrap: boolean,
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('offset', shaderSources.quadVert, shaderSources.offset);
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uShift: new Float32Array([shiftX, shiftY]),
      uEdge: wrap ? 0 : 1,
    });
    this.quad.draw();
  }

  applyMask(target: Framebuffer, source: Texture, mask: Texture): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('maskApply', shaderSources.quadVert, shaderSources.maskApply);
    program.use();
    source.bind(0);
    mask.bind(1);
    program.setUniforms({ uTransform: QUAD_TO_TARGET, uSource: 0, uMask: 1 });
    this.quad.draw();
  }

  /** Samples `source` through a displacement field (RG = pixel offsets). */
  applyLiquify(target: Framebuffer, source: Texture, displace: Texture): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('liquify', shaderSources.quadVert, shaderSources.liquify);
    program.use();
    source.bind(0);
    displace.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uDisplace: 1,
      uTexel: new Float32Array([1 / source.width, 1 / source.height]),
    });
    this.quad.draw();
  }

  /** Paints one liquify dab into the displacement field. */
  liquifyBrush(
    target: Framebuffer,
    displace: Texture,
    freeze: Texture | null,
    params: {
      mode: number;
      centerX: number;
      centerY: number;
      deltaX: number;
      deltaY: number;
      radius: number;
      strength: number;
      angle: number;
      docWidth: number;
      docHeight: number;
    },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'liquifyBrush',
      shaderSources.quadVert,
      shaderSources.liquifyBrush,
    );
    program.use();
    displace.bind(0);
    (freeze ?? this.whiteMask).bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uDisplace: 0,
      uFreeze: 1,
      uUseFreeze: freeze != null,
      uMode: params.mode,
      uCenter: new Float32Array([params.centerX, params.centerY]),
      uDelta: new Float32Array([params.deltaX, params.deltaY]),
      uRadius: params.radius,
      uStrength: params.strength,
      uAngle: params.angle,
      uDocSize: new Float32Array([params.docWidth, params.docHeight]),
    });
    this.quad.draw();
  }

  /** Soft circular dab into the liquify freeze mask (`value` 1 freezes, 0 thaws). */
  liquifyFreeze(
    target: Framebuffer,
    freeze: Texture,
    params: {
      centerX: number;
      centerY: number;
      radius: number;
      value: number;
      strength: number;
      docWidth: number;
      docHeight: number;
    },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'liquifyFreeze',
      shaderSources.quadVert,
      shaderSources.liquifyFreeze,
    );
    program.use();
    freeze.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uFreeze: 0,
      uCenter: new Float32Array([params.centerX, params.centerY]),
      uRadius: params.radius,
      uValue: params.value,
      uStrength: params.strength,
      uDocSize: new Float32Array([params.docWidth, params.docHeight]),
    });
    this.quad.draw();
  }

  /** Samples a coverage or alpha channel into an R8 target, with optional UV map. */
  sampleMask(
    target: Framebuffer,
    source: Texture,
    options: {
      srcMatrix?: Mat3 | null;
      fromAlpha?: boolean;
      invert?: boolean;
    } = {},
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get(
      'maskSample',
      shaderSources.quadVert,
      shaderSources.maskSample,
    );
    program.use();
    source.bind(0);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uUseMatrix: Boolean(options.srcMatrix),
      uSrcMatrix: options.srcMatrix ?? this.identity,
      uFromAlpha: options.fromAlpha !== false,
      uInvert: Boolean(options.invert),
    });
    this.quad.draw();
  }

  /** Tints an R8 coverage mask into a straight-alpha colour layer. */
  fxTint(
    target: Framebuffer,
    coverage: Texture,
    source: Texture,
    params: { color: RGBA; opacity: number; clip: 0 | 1 | 2 },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('fxTint', shaderSources.quadVert, shaderSources.fxTint);
    program.use();
    coverage.bind(0);
    source.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uCoverage: 0,
      uSource: 1,
      uColor: new Float32Array([params.color.r, params.color.g, params.color.b, params.color.a]),
      uOpacity: params.opacity,
      uClip: params.clip,
    });
    this.quad.draw();
  }

  fxStroke(
    target: Framebuffer,
    alpha: Texture,
    blurred: Texture,
    params: { color: RGBA; opacity: number; position: 0 | 1 | 2; soft: number },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('fxStroke', shaderSources.quadVert, shaderSources.fxStroke);
    program.use();
    alpha.bind(0);
    blurred.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uAlpha: 0,
      uBlurred: 1,
      uColor: new Float32Array([params.color.r, params.color.g, params.color.b, params.color.a]),
      uOpacity: params.opacity,
      uPosition: params.position,
      uSoft: params.soft,
    });
    this.quad.draw();
  }

  fxBevel(
    target: Framebuffer,
    alpha: Texture,
    source: Texture,
    params: {
      depth: number;
      opacity: number;
      lightX: number;
      lightY: number;
      highlight: RGBA;
      highlightOpacity: number;
      shadow: RGBA;
      shadowOpacity: number;
    },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('fxBevel', shaderSources.quadVert, shaderSources.fxBevel);
    program.use();
    alpha.bind(0);
    source.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uAlpha: 0,
      uSource: 1,
      uTexel: new Float32Array([1 / alpha.width, 1 / alpha.height]),
      uLight: new Float32Array([params.lightX, params.lightY]),
      uDepth: params.depth,
      uOpacity: params.opacity,
      uHighlight: new Float32Array([
        params.highlight.r,
        params.highlight.g,
        params.highlight.b,
        params.highlight.a,
      ]),
      uShadow: new Float32Array([
        params.shadow.r,
        params.shadow.g,
        params.shadow.b,
        params.shadow.a,
      ]),
      uHighlightOpacity: params.highlightOpacity,
      uShadowOpacity: params.shadowOpacity,
    });
    this.quad.draw();
  }

  fxOverlay(target: Framebuffer, source: Texture, overlay: Texture, opacity: number): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('fxOverlay', shaderSources.quadVert, shaderSources.fxOverlay);
    program.use();
    source.bind(0);
    overlay.bind(1);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uOverlay: 1,
      uOpacity: opacity,
    });
    this.quad.draw();
  }

  /**
   * Destructive filter pass. `aux` is the blurred source for the filters that
   * need a neighbourhood average; leave it null for everything else.
   */
  applyFilter(
    target: Framebuffer,
    source: Texture,
    selection: Texture | null,
    pass: {
      type: number;
      params: Float32Array;
      aux: Texture | null;
      seed: number;
    },
  ): void {
    const { gl } = this;
    gl.disable(gl.BLEND);
    target.bind();
    const program = this.programs.get('filter', shaderSources.quadVert, shaderSources.filter);
    program.use();
    source.bind(0);
    (pass.aux ?? source).bind(1);
    (selection ?? this.whiteMask).bind(2);
    program.setUniforms({
      uTransform: QUAD_TO_TARGET,
      uSource: 0,
      uAux: 1,
      uUseAux: pass.aux != null,
      uSelection: 2,
      uUseSelection: selection !== null,
      uType: pass.type,
      uParams: pass.params,
      uTexel: new Float32Array([1 / source.width, 1 / source.height]),
      uSeed: pass.seed,
    });
    this.quad.draw();
  }

  dispose(): void {
    this.programs.dispose();
    this.quad.dispose();
    this.mesh.dispose();
    this.whiteMask.dispose();
  }
}
